import { motion } from "framer-motion";
import { ExternalLink, Youtube, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { YOUTUBE_VIDEOS, getYoutubeEmbedUrl } from "@/data/youtubeVideos";
import { PRODUCT_GUIDE } from "@/constants/testIds";

const DENSO_CATALOGUE_URL = "https://denso-sales.co.za/downloads";

export default function ProductGuide() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-10">
      <div className="space-y-2 text-center">
        <span className="inline-block text-xs uppercase tracking-[0.2em] font-semibold text-denso-red bg-red-50 px-3 py-1 rounded-full">
          Product Catalogue
        </span>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">DENSO Catalogue</h1>
        <p className="text-slate-600 text-base max-w-xl mx-auto">
          Browse the full DENSO product range, part numbers and brochures on the official DENSO Sales
          catalogue and downloads portal.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="bg-denso-red rounded-xl p-8 sm:p-10 text-center space-y-4"
      >
        <BookOpen className="h-10 w-10 text-white mx-auto" />
        <h2 className="font-heading text-2xl font-bold text-white">Open the DENSO Catalogue</h2>
        <p className="text-white/85 text-sm max-w-lg mx-auto">
          Opens the official DENSO Sales downloads page in a new tab, where you can view and download
          the latest product catalogues and brochures.
        </p>
        <Button
          data-testid={PRODUCT_GUIDE.catalogueButton}
          onClick={() => window.open(DENSO_CATALOGUE_URL, "_blank", "noopener,noreferrer")}
          className="bg-white text-denso-red hover:bg-slate-100 font-semibold h-11 px-6 transition-all duration-200 hover:-translate-y-0.5"
        >
          Open DENSO Catalogue <ExternalLink className="h-4 w-4" />
        </Button>
      </motion.div>

      <div className="space-y-4">
        <div className="space-y-2 text-center">
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-denso-red bg-red-50 px-3 py-1 rounded-full">
            <Youtube className="h-3.5 w-3.5" /> Official DENSO Videos
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Watch & Learn More</h2>
          <p className="text-slate-600 text-sm max-w-xl mx-auto">
            A curated selection of official DENSO videos covering products, technology and quality standards.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          {YOUTUBE_VIDEOS.map((video) => (
            <div
              key={video.id}
              data-testid={PRODUCT_GUIDE.youtubeCard(video.id)}
              className="bg-white rounded-md border border-slate-200 overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-md"
            >
              <div className="relative aspect-video w-full bg-black">
                <iframe
                  data-testid={PRODUCT_GUIDE.youtubeIframe(video.id)}
                  src={getYoutubeEmbedUrl(video.id)}
                  title={video.title}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <div className="p-4 space-y-1">
                <h3 className="font-semibold text-slate-800 text-sm">{video.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{video.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
